import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, ArrowRight, BellRing, CalendarClock, Download, Gauge, Loader2,
  ShieldCheck, Target, TrendingUp, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPut, apiDelete } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * Phase 10 — Parent Intelligence.
 *
 * Insight, not surveillance. Every figure on this page is one the child can
 * already see in their own progress view, readiness is a RANGE with its drivers
 * (or an honest "not enough evidence yet"), and there is no hours-only report,
 * no idle time, no reminder counts and nothing hidden from the student.
 */

const Card = ({ title, icon: Icon, children, action, testId }) => (
  <div className="rounded-3xl border border-slate-200 bg-white p-4 sm:p-5" data-testid={testId}>
    <div className="flex items-center justify-between gap-2">
      <p className="font-heading text-[13px] font-semibold text-slate-800 flex items-center gap-1.5">
        <Icon className="w-4 h-4 text-indigo-500" /> {title}
      </p>
      {action}
    </div>
    <div className="mt-2.5">{children}</div>
  </div>
);

export default function ParentInsightPage() {
  const [children, setChildren] = useState([]);
  const [childId, setChildId] = useState('');
  const [data, setData] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    apiGet('/parent/children')
      .then(d => {
        const kids = d.children || [];
        setChildren(kids);
        if (kids.length) setChildId(kids[0].child_id || kids[0].user_id || '');
      })
      .catch(() => setChildren([]));
  }, []);

  const load = useCallback(async () => {
    const q = childId ? `?child_id=${childId}` : '';
    try {
      const [insight, p] = await Promise.all([
        apiGet(`/premium/v1/parent/intelligence${q}`),
        apiGet(`/premium/v1/parent/notify-prefs${q}`),
      ]);
      setData(insight); setPrefs(p); setError('');
    } catch (e) { setError(e?.message || 'That insight is unavailable right now'); }
  }, [childId]);

  useEffect(() => { load(); }, [load]);

  const savePrefs = async (patch) => {
    const q = childId ? `?child_id=${childId}` : '';
    try {
      const res = await apiPut(`/premium/v1/parent/notify-prefs${q}`, patch);
      setPrefs(p => ({ ...p, prefs: res.prefs }));
      toast.success('Saved');
    } catch (e) { toast.error(e?.message || 'Could not save that'); }
  };

  const exportAll = async () => {
    const q = childId ? `?child_id=${childId}` : '';
    try {
      const res = await apiGet(`/premium/v1/parent/intelligence/export${q}`);
      const url = URL.createObjectURL(new Blob([JSON.stringify(res, null, 2)], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url; a.download = `gurukulveda-insight-${res.child_id}.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.message || 'Could not export that'); }
  };

  const deleteAll = async () => {
    if (!window.confirm('Remove the reminders and readiness history we hold on your side? Your child\'s own learning record is untouched.')) return;
    const q = childId ? `?child_id=${childId}` : '';
    try {
      const res = await apiDelete(`/premium/v1/parent/intelligence${q}`);
      toast.success(res.message); load();
    } catch (e) { toast.error(e?.message || 'Could not delete that'); }
  };

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10" data-testid="parent-insight-error">
        <p className="font-heading text-base text-slate-800">We couldn't open that insight</p>
        <p className="font-body text-sm text-slate-500 mt-1">{error}</p>
        <button onClick={load} className="mt-3 text-sm font-heading text-indigo-600 hover:underline" data-testid="parent-insight-retry">Try again</button>
      </div>
    );
  }
  if (!data || !prefs) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 flex items-center gap-2 text-slate-400" data-testid="parent-insight-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> <span className="font-body text-sm">Gathering the evidence…</span>
      </div>
    );
  }

  const r = data.readiness;
  const p = prefs.prefs;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="parent-insight-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          {data.student_name?.split(' ')[0] || 'Your child'}'s readiness
        </h1>
        <p className="font-body text-sm text-slate-500 mt-1">{data.privacy}</p>
        {children.length > 1 && (
          <div className="flex gap-1.5 mt-3" data-testid="parent-insight-child-switch">
            {children.map(c => (
              <button key={c.child_id || c.user_id} onClick={() => setChildId(c.child_id || c.user_id)}
                data-testid={`parent-insight-child-${c.child_id || c.user_id}`}
                className={`text-[11px] font-heading px-3 py-1 rounded-full border ${childId === (c.child_id || c.user_id) ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}>
                {c.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* readiness — range or honest refusal */}
      <Card title="Exam readiness" icon={Gauge} testId="parent-readiness">
        {r.status === 'estimated' ? (
          <>
            <p className="font-display text-3xl font-bold text-slate-900" data-testid="parent-readiness-range">{r.low}–{r.high}%</p>
            <p className="font-body text-[12px] text-slate-500">{r.uncertainty} Confidence: {r.confidence}.</p>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="parent-readiness-drivers">
              {r.drivers.map((d, i) => (
                <span key={i} className={`text-[11px] font-body px-2 py-0.5 rounded-full border ${d.direction === 'up' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : d.direction === 'down' ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                  {d.label}
                </span>
              ))}
            </div>
          </>
        ) : (
          <>
            <p className="font-heading text-base text-slate-800" data-testid="parent-readiness-insufficient">{r.message}</p>
            <p className="font-body text-[13px] text-slate-600 mt-1">{r.next_evidence_action}</p>
            <p className="font-body text-[11px] text-slate-400 mt-1">
              Evidence so far: {r.basis.graded_concepts} graded concept(s) over {r.basis.study_days} day(s).
              We'd rather say nothing than guess.
            </p>
          </>
        )}
      </Card>

      {/* what a parent can do */}
      <Card title="What would actually help" icon={Target} testId="parent-actions">
        <div className="space-y-1.5">
          {data.suggested_actions.map((a, i) => (
            <div key={i} className="rounded-2xl border border-indigo-100 bg-indigo-50/60 p-2.5" data-testid={`parent-action-${i}`}>
              <p className="font-heading text-[13px] text-slate-800">{a.title}</p>
              <p className="font-body text-[12px] text-slate-600">{a.why}</p>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid sm:grid-cols-2 gap-3">
        <Card title="Exams ahead" icon={CalendarClock} testId="parent-exams">
          {data.exams.length ? (
            <ul className="space-y-1.5">
              {data.exams.map(e => (
                <li key={e.exam_id} className="font-body text-[13px] text-slate-700" data-testid={`parent-exam-${e.exam_id}`}>
                  {e.name} · <strong>{e.days_to_exam} day(s)</strong> · portion {e.portion_coverage_pct}%
                </li>
              ))}
            </ul>
          ) : <p className="font-body text-[13px] text-slate-500">No exam mapped yet.</p>}
        </Card>

        <Card title="Plan followed" icon={TrendingUp} testId="parent-adherence">
          <p className="font-heading text-2xl font-bold text-slate-900">{data.adherence.pct}%</p>
          <p className="font-body text-[12px] text-slate-500">
            {data.adherence.done} of {data.adherence.due} planned pieces done · {data.minutes.week_done}/{data.minutes.week_target} minutes this week
          </p>
          <p className="font-body text-[11px] text-slate-400 mt-1">{data.minutes.note}</p>
        </Card>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <Card title="Concepts" icon={ShieldCheck} testId="parent-concepts">
          <p className="font-body text-[13px] text-slate-700">
            {data.concepts.mastered} mastered · {data.concepts.learning} part-learnt · {data.concepts.weak} weak
          </p>
          <div className="mt-2 space-y-1">
            {data.subject_strength.map(s => (
              <div key={s.subject} className="flex items-center gap-2" data-testid={`parent-subject-${s.subject}`}>
                <span className="font-body text-[12px] text-slate-600 w-24 truncate">{s.subject}</span>
                <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-indigo-400 to-emerald-400" style={{ width: `${s.mastery}%` }} />
                </div>
                <span className="font-body text-[11px] text-slate-500 w-20 text-right">{s.mastery}% · {s.state}</span>
              </div>
            ))}
          </div>
          <p className="font-body text-[12px] text-slate-500 mt-2">{data.quiz_trend.message}</p>
        </Card>

        <Card title="Consistency" icon={AlertTriangle} testId="parent-consistency">
          <p className="font-body text-[13px] text-slate-700">
            {data.consistency.streak_days}-day run (best {data.consistency.best}) ·
            {' '}{data.consistency.days_at_risk} day(s) went by untouched
          </p>
          {data.revision_overdue > 0 && (
            <p className="font-body text-[12px] text-amber-800 mt-1.5" data-testid="parent-overdue">
              {data.revision_overdue} revision item(s) overdue.
            </p>
          )}
          {data.mock_results.length > 0 && (
            <ul className="mt-2 space-y-1">
              {data.mock_results.slice(0, 3).map((m, i) => (
                <li key={i} className="font-body text-[12px] text-slate-600" data-testid={`parent-mock-${i}`}>
                  {m.exam_name}: {m.percentage}% ({m.recorded_on})
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {data.weak_concepts.length > 0 && (
        <Card title="What's shaky (as your child sees it too)" icon={AlertTriangle} testId="parent-weak">
          <ul className="space-y-1">
            {data.weak_concepts.map(w => (
              <li key={w.concept_id} className="font-body text-[13px] text-slate-700" data-testid={`parent-weak-${w.concept_id}`}>
                {w.title} <span className="text-slate-400">· {w.subject} · {w.mastery}%</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* reminders */}
      <Card title="Reminders" icon={BellRing} testId="parent-prefs"
        action={<button onClick={() => navigate('/cockpit')} className="text-[11px] font-heading text-indigo-600 hover:underline" data-testid="parent-open-cockpit">
          See their day →
        </button>}>
        <div className="space-y-2">
          {[['in_app', 'In the app (live)'], ['email', 'By email (opt-in)']].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer" data-testid={`parent-pref-${key}`}>
              <input type="checkbox" checked={!!p[key]} onChange={e => savePrefs({ [key]: e.target.checked })} />
              <span className="font-body text-[13px] text-slate-700">{label}</span>
            </label>
          ))}
          <p className="font-body text-[12px] text-slate-400" data-testid="parent-pref-whatsapp">
            WhatsApp — {prefs.channels.whatsapp}. We'll ask you to opt in when it is.
          </p>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            {[['quiet_from_hour', 'Quiet from'], ['quiet_to_hour', 'Quiet until'], ['preferred_study_hour', 'Usual study hour']].map(([key, label]) => (
              <label key={key} className="flex items-center gap-1.5">
                <span className="font-body text-[11px] text-slate-500">{label}</span>
                <Input type="number" min={0} max={23} defaultValue={p[key]} className="w-16 h-8 rounded-xl text-sm"
                  data-testid={`parent-pref-${key}`}
                  onBlur={e => savePrefs({ [key]: Number(e.target.value) })} />
              </label>
            ))}
          </div>

          <div className="flex flex-wrap gap-1.5 pt-1">
            {Object.entries(p.kinds).map(([kind, on]) => (
              <button key={kind} onClick={() => savePrefs({ kinds: { [kind]: !on } })}
                data-testid={`parent-kind-${kind}`}
                className={`text-[11px] font-heading px-2.5 py-1 rounded-full border ${on ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-white border-slate-200 text-slate-400'}`}>
                {kind.replace('_', ' ')}
              </button>
            ))}
          </div>

          <div className="flex gap-2 pt-2">
            <Button size="sm" variant="outline" className="rounded-xl h-8" onClick={exportAll} data-testid="parent-export-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export everything
            </Button>
            <Button size="sm" variant="outline" className="rounded-xl h-8 text-rose-600" onClick={deleteAll} data-testid="parent-delete-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete my side
            </Button>
          </div>
        </div>
      </Card>

      <button onClick={() => navigate('/growth')} data-testid="parent-open-growth"
        className="inline-flex items-center gap-1.5 text-[12px] font-heading text-indigo-600 hover:underline">
        See the same progress your child sees <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}
